import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IonFab, ModalController } from '@ionic/angular';
import * as L from 'leaflet';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MapLayerRegistryService } from './services/map-layer-registry.service';
import { MapHoverService } from './services/map-hover.service';
import { LeafletCaptureService } from './services/leaflet-capture.service';
import { LeafletRelayService } from './services/leaflet-relay.service';
import { MapLayerCategoryEnum } from './models/map-layer-category.enum';
import { MapLayerRefs } from './models/map-layer-refs.model';
import { MapLayerGroup } from './models/map-layer-group.model';
import { MapLayerDefinition } from './models/map-layer-definition.model';
import { MapLayerPresetType } from './models/map-layer-preset.type';
import { MAP_STYLES, infraTypeKey } from './models/map-styles';
import { PageStatusService } from 'src/app/services/page-status.service';
import { LeafletService as LeafletMapService } from 'src/app/services/leaflet.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'src/app/services/toasts.service';
import { Router } from '@angular/router';
import { Google } from 'src/app/utils/google.js';
import { RelayTooltipComponent } from 'src/app/components/relay-tooltip/relay-tooltip.component';
import { WindyComponent } from '../windy/windy.component';
import { createColoredDroneIcon, createDroneIcon, updateDroneIconColor } from 'src/assets/icons';
import { Icons } from '../trajectory-creation/icons.model';
import { Colors, OperationStatusColorCodeMap, OrganizationTypeEnum, User } from '@mocca-technologies/model';
import * as icons from 'src/assets/icons';

/**
 * Main Leaflet map component.
 *
 * This refactor preserves the existing public behavior while introducing:
 * - a custom Angular layers panel
 * - a central layer registry
 * - extracted helper services for capture/overlay/relay logic
 *
 * IMPORTANT:
 * - Replace the `any` placeholders with your real project types/imports.
 * - Replace helper globals like `createDroneIcon`, `updateDroneIconColor`, `createColoredDroneIcon`,
 *   `WindyComponent`, `Google`, `Icons`, `Colors`, `environment`, etc. with your real imports.
 */
@Component({
  selector: 'app-leaflet-map',
  templateUrl: './leaflet-map.component.html',
  styleUrls: ['./leaflet-map.component.scss'],
  providers: [MapLayerRegistryService, MapHoverService],
})
export class LeafletMapComponent implements OnInit, OnDestroy {
  /** Child component access. */
  @ViewChild('mapContainer', { static: true }) mapContainer: ElementRef;

  /** Legacy FAB reference preserved for compatibility with existing template code. */
  @ViewChild('layersFab') layersFab: IonFab;

  /** Controls which layer categories are registered in the panel.
   * - 'full'  → coordination + infrastructure + near-search capable pages
   * - 'relay' → adds relay layers to the panel
   * - 'police'→ adds PSO layers to the panel
   * - 'base'  → base set only (default)
   */
  @Input() panelMode: 'full' | 'relay' | 'police' | 'base' = 'base';

  /** Outputs preserved from the current component contract. */
  @HostBinding('class.no-center-fab') get hideCenterFab(): boolean { return this.pageStatusService.isCreateTrajectoryPage(); }

  @Output() mapReady = new EventEmitter<L.Map>();
  @Output() mapClick = new EventEmitter<L.LeafletMouseEvent>();
  @Output() layersPanelChange = new EventEmitter<boolean>();
  @Output() operationSelected = new EventEmitter<string>();
  @Output() communicationSelected = new EventEmitter<any>();
  @Output() trajectorySelected = new EventEmitter<string>();
  @Output() coordinationSelected = new EventEmitter<string>();
  @Output() coordinationClosed = new EventEmitter<void>();
  @Output() centerMap = new EventEmitter<void>();
  @Output() basicAreaClicked = new EventEmitter<string>();

  /** Current user loaded from storage. */
  user: User;

  /** Project icon/color helpers. Replace with your real imports/types. */
  icons = new Icons();
  colors = new Colors();

  /** Live Leaflet map instance. */
  leafletmap: L.Map;

  /** Optional Leaflet scale control. */
  scaleControl: L.Control.Scale;

  /** Search results and state preserved from the current component. */
  locationSearchItems: any[] = [];
  locationSearchValue = '';
  readonly searchTerms = new Subject<string>();
  private readonly DEBOUNCE_TIME_MS = 300;

  /** Visual state preserved from the current component. */
  activeFab: 'none' | 'layers' | 'opacity' | 'location' | 'center' | 'restart' | 'windy' = 'none';
  showLayersPanel = false;
  layerGroups: MapLayerGroup[] = [];

  /** Internal destroy notifier for subscriptions. */
  private readonly destroy$ = new Subject<void>();

  /** Default map center preserved from the current implementation. */
  readonly catCenter = L.latLng(41.82046, 1.86768);
  readonly catRadius = 210000;

  /** All layer groups preserved from the current component. */
  readonly baseMapLayerGroup = new L.LayerGroup();
  readonly apOperationLayerGroup = new L.LayerGroup();
  readonly apTrajectoryLayerGroup = new L.LayerGroup();
  readonly coordinationConflictLayer = new L.LayerGroup();
  readonly polygonLayer = new L.LayerGroup<L.Polygon>();
  readonly structuresLayerGroup = new L.LayerGroup();
  readonly protectionLowLayerGroup = new L.LayerGroup();
  readonly protectionHighLayerGroup = new L.LayerGroup();
  readonly ctrAtzLayerGroup = new L.LayerGroup();
  readonly naturalParksLayerGroup = new L.LayerGroup();
  readonly harboursLayerGroup = new L.LayerGroup();
  readonly trackNetworkLayerGroup = new L.LayerGroup();
  readonly innerZonesLayerGroup = new L.LayerGroup();
  readonly runwayLayerGroup = new L.LayerGroup();
  readonly otherLayerGroup = new L.LayerGroup();
  readonly markersLayerGroup = new L.LayerGroup();
  readonly basicAreaLevel0LayerGroup = new L.LayerGroup();
  readonly basicAreaLevel1LayerGroup = new L.LayerGroup();
  readonly selectedBasicAreaLevel0Layer = new L.LayerGroup();
  readonly selectedBasicAreaLevel1Layer = new L.LayerGroup();
  readonly communicationsLayerGroup = new L.LayerGroup();
  readonly externalRadiusLayer = new L.LayerGroup();
  readonly externalRectangleLayer = new L.LayerGroup();
  readonly externalConvexPolygonLayer = new L.LayerGroup();
  readonly reducedRegionApproximationLayer = new L.LayerGroup();
  readonly regionMapLayer = new L.LayerGroup();
  readonly relayVehiclesLayerGroup = new L.LayerGroup();
  readonly relayTracesLayerGroup = new L.LayerGroup();
  readonly urbanAreasLayerGroup = new L.LayerGroup();
  readonly urbanAreasRegistryLayerGroup = new L.LayerGroup();

  /** Existing maps used by the old implementation and preserved here. */
  readonly operationMarkerMap: Map<string, L.Marker> = new Map();
  readonly communicationMarkers: Map<string, L.Marker> = new Map();
  selectedCommunicationId: string = null;

  /** Hover marker for chart point highlighting. */
  private hoverMarker: L.CircleMarker | null = null;

  /** Legacy mapLayers categorization preserved for clear helpers. */
  readonly mapLayers: any = {
    infrastructures: [
      this.structuresLayerGroup,
      this.innerZonesLayerGroup,
      this.runwayLayerGroup,
      this.protectionLowLayerGroup,
      this.protectionHighLayerGroup,
      this.ctrAtzLayerGroup,
      this.naturalParksLayerGroup,
      this.harboursLayerGroup,
      this.trackNetworkLayerGroup,
      this.otherLayerGroup,
      this.markersLayerGroup,
    ],
    operations: [
      this.apOperationLayerGroup,
      this.apTrajectoryLayerGroup,
      this.coordinationConflictLayer,
      this.polygonLayer,
    ],
    pso: [
      this.basicAreaLevel0LayerGroup,
      this.basicAreaLevel1LayerGroup,
      this.communicationsLayerGroup,
      this.externalRadiusLayer,
      this.externalRectangleLayer,
      this.externalConvexPolygonLayer,
      this.reducedRegionApproximationLayer,
      this.regionMapLayer,
    ],
  };

  /** Base maps preserved from the current implementation. */
  readonly leafletBaseLayers = {
    'Google Maps': new Google('ROADMAP', { id: 'base', maxZoom: 21 }),
    'Google Satellite': new Google('SATELLITE', { id: 'base', maxZoom: 21 }),
    'Open Street Map': new L.TileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      id: 'base',
      maxZoom: 21,
      attribution: 'Open Street Map',
    }),
    'Arc GIS': new L.TileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}.png',
      {
        id: 'base',
        maxNativeZoom: 18,
        maxZoom: 21,
        attribution: 'Arc GIS',
      }
    ),
  };

  /** Currently selected base layer, preserved for backward compatibility. */
  selectedBaseLayer = this.leafletBaseLayers['Google Satellite'];

  /** Leaflet map options. */
  leafletOptions: L.MapOptions = {
    attributionControl: true,
    zoomControl: false,
    layers: [this.leafletBaseLayers['Google Satellite']],
    center: this.catCenter,
    zoom: 12,
    maxBoundsViscosity: 1.0,
    zoomDelta: 0.5,
    wheelPxPerZoomLevel: 100,
  };

  /** Dynamic relay tooltip infrastructure preserved from the current component. */
  tooltipFactory = this.resolver.resolveComponentFactory(RelayTooltipComponent);
  tooltipProps = { autoClose: false, className: 'onclick-map-popup' };

  public setLayerVisibility(layerId: string, visible: boolean): void {
    this.layerRegistry.setOverlayVisibility(layerId, visible);
  }

  public setLayersVisibility(layerIds: string[], visible: boolean): void {
    layerIds.forEach(layerId => this.layerRegistry.setOverlayVisibility(layerId, visible));
  }

  /**
   * Creates the component.
   *
   * Keep your original domain services here too. The constructor below preserves
   * the services needed by the current HTML and main logic.
   */
  constructor(
    public readonly pageStatusService: PageStatusService,
    private readonly modalCtrl: ModalController,
    private readonly resolver: ComponentFactoryResolver,
    private readonly injector: Injector,
    private readonly layerRegistry: MapLayerRegistryService,
    private readonly hoverService: MapHoverService,
    private readonly captureService: LeafletCaptureService,
    private readonly relayService: LeafletRelayService,
    private readonly leafletMapService: LeafletMapService,
    private readonly translateService: TranslateService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  /** Initializes subscriptions and fixed z-indexes. */
  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem('user'));

    this.searchTerms
      .pipe(debounceTime(this.DEBOUNCE_TIME_MS), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.performLocationSearch(term);
      });

    this.basicAreaLevel0LayerGroup.setZIndex(10);
    this.selectedBasicAreaLevel0Layer.setZIndex(15);
    this.basicAreaLevel1LayerGroup.setZIndex(20);
    this.selectedBasicAreaLevel1Layer.setZIndex(25);
  }

  /** Releases subscriptions and map listeners. */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.leafletmap) {
      this.leafletmap.off();
      this.relayService.clearAll(this.leafletmap);
    }

    this.layerRegistry.clear();
  }

  /** Initializes Leaflet and synchronizes the registry with the map. */
  onMapReady(map: L.Map): void {
    this.leafletmap = map;
    this.leafletmap.setMinZoom(3);

    const southWest = L.latLng(-89.98155760646617, -180);
    const northEast = L.latLng(89.99346179538875, 180);
    this.leafletmap.setMaxBounds(L.latLngBounds(southWest, northEast));

    // Named panes for semantic z-ordering (drawn bottom-to-top).
    this.leafletmap.createPane('pane-other').style.zIndex = '400';
    this.leafletmap.createPane('pane-ctr-atz').style.zIndex = '401';
    this.leafletmap.createPane('pane-nat-parks').style.zIndex = '402';
    this.leafletmap.createPane('pane-urban-areas').style.zIndex = '403';
    this.leafletmap.createPane('pane-harbours-tracks').style.zIndex = '404';
    this.leafletmap.createPane('pane-structures').style.zIndex = '405';
    this.leafletmap.createPane('pane-inner-zones').style.zIndex = '406';
    this.leafletmap.createPane('pane-protection-high').style.zIndex = '407';
    this.leafletmap.createPane('pane-protection-low').style.zIndex = '408';

    // Operations layers are always on — added directly, not managed by the registry.
    this.mapLayers.operations.forEach((layer) => layer.addTo(this.leafletmap));

    // Infrastructure marker pins are always on — not in the panel.
    this.markersLayerGroup.addTo(this.leafletmap);

    this.initializeLayerRegistry();
    this.syncRegistryToMap();

    if (!this.scaleControl) {
      this.scaleControl = L.control.scale({ imperial: false });
      this.scaleControl.addTo(this.leafletmap);
    }

    this.layerRegistry.layers$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.layerGroups = this.layerRegistry.getGroupedLayers();
      this.syncRegistryToMap();
    });

    setTimeout(() => this.leafletmap.invalidateSize(true), 500);

    // Map-level mousemove drives the hover panel; fires for ALL shapes under cursor.
    if (this.panelMode === 'full') {
      this.leafletmap.on('mousemove', (e: L.LeafletMouseEvent) => {
        this.hoverService.updateFromLatLng(e.latlng);
      });
    }

    // Re-emit map click as an Angular output so parent components can bind to it
    // reliably without holding a reference to the internal L.Map instance.
    this.leafletmap.on('click', (e: L.LeafletMouseEvent) => {
      this.mapClick.emit(e);
    });

    // layerGroups was updated synchronously above (initializeLayerRegistry + BehaviorSubject
    // immediate emission on subscribe). Force a clean CD pass so the verification pass sees
    // the current value as the baseline and does not throw NG0100.
    this.cdr.detectChanges();

    this.mapReady.emit(this.leafletmap);
  }

  /** Registers all layers shown in the custom layers panel. */
  private initializeLayerRegistry(): void {
    const isDevel = this.user?.profile === 'devel';

    const layers: MapLayerDefinition[] = [
      { id: 'base-google-satellite', label: 'Google Satellite', category: MapLayerCategoryEnum.BASE, type: 'base' as const, visible: true, order: 1, layer: this.leafletBaseLayers['Google Satellite'] },
      { id: 'base-google-maps', label: 'Google Maps', category: MapLayerCategoryEnum.BASE, type: 'base' as const, visible: false, order: 2, layer: this.leafletBaseLayers['Google Maps'] },
      { id: 'base-osm', label: 'Open Street Map', category: MapLayerCategoryEnum.BASE, type: 'base' as const, visible: false, order: 3, layer: this.leafletBaseLayers['Open Street Map'] },
      { id: 'base-arcgis', label: 'Arc GIS', category: MapLayerCategoryEnum.BASE, type: 'base' as const, visible: false, order: 4, layer: this.leafletBaseLayers['Arc GIS'] },
    ];

    if (this.panelMode === 'full') {
      // full mode: coordination areas + infrastructure are panel-toggleable
      layers.push(
        { id: 'coord-protection-low', label: 'Protection – Low', category: MapLayerCategoryEnum.COORDINATION_AREAS, type: 'overlay' as const, visible: true, order: 10, layer: this.protectionLowLayerGroup },
        { id: 'coord-protection-high', label: 'Protection – High', category: MapLayerCategoryEnum.COORDINATION_AREAS, type: 'overlay' as const, visible: true, order: 11, layer: this.protectionHighLayerGroup },
        { id: 'coord-ctr-atz', label: 'CTR / ATZ', category: MapLayerCategoryEnum.COORDINATION_AREAS, type: 'overlay' as const, visible: true, order: 12, layer: this.ctrAtzLayerGroup },
        { id: 'coord-natural-parks', label: 'Natural parks', category: MapLayerCategoryEnum.COORDINATION_AREAS, type: 'overlay' as const, visible: true, order: 13, layer: this.naturalParksLayerGroup },
        { id: 'coord-harbours', label: 'Harbours', category: MapLayerCategoryEnum.COORDINATION_AREAS, type: 'overlay' as const, visible: true, order: 14, layer: this.harboursLayerGroup },
        { id: 'coord-track-network', label: 'Track network', category: MapLayerCategoryEnum.COORDINATION_AREAS, type: 'overlay' as const, visible: true, order: 15, layer: this.trackNetworkLayerGroup },
        { id: 'infra-structures', label: 'Structures', category: MapLayerCategoryEnum.INFRASTRUCTURE, type: 'overlay' as const, visible: true, order: 20, layer: this.structuresLayerGroup },
        { id: 'infra-inner-zones', label: 'Inner zones', category: MapLayerCategoryEnum.INFRASTRUCTURE, type: 'overlay' as const, visible: true, order: 21, layer: this.innerZonesLayerGroup },
        { id: 'infra-runways', label: 'Runways / Helipads', category: MapLayerCategoryEnum.INFRASTRUCTURE, type: 'overlay' as const, visible: true, order: 22, layer: this.runwayLayerGroup },
      );
    } else {
      // base/relay/police: layers always on but not in the panel
      [
        this.protectionLowLayerGroup, this.protectionHighLayerGroup,
        this.ctrAtzLayerGroup, this.naturalParksLayerGroup,
        this.harboursLayerGroup, this.trackNetworkLayerGroup,
        this.structuresLayerGroup, this.innerZonesLayerGroup,
        this.runwayLayerGroup, this.otherLayerGroup, this.urbanAreasLayerGroup, this.urbanAreasRegistryLayerGroup,
      ].forEach(lg => lg.addTo(this.leafletmap));
    }

    // dummy array close — layers is still open below for relay/police/debug
    const _layers = layers;

    // Relay — only shown in the panel on relay pages
    if (this.panelMode === 'relay') {
      _layers.push(
        { id: 'relay-vehicles', label: 'Relay vehicles', category: MapLayerCategoryEnum.RELAY, type: 'overlay' as const, visible: true, order: 40, layer: this.relayVehiclesLayerGroup },
        { id: 'relay-traces', label: 'Relay traces', category: MapLayerCategoryEnum.RELAY, type: 'overlay' as const, visible: true, order: 41, layer: this.relayTracesLayerGroup },
      );
    } else {
      // Always add relay groups to the map so the relay service can still draw into them
      this.relayVehiclesLayerGroup.addTo(this.leafletmap);
      this.relayTracesLayerGroup.addTo(this.leafletmap);
    }

    // Police areas — only on police pages
    if (this.panelMode === 'police') {
      _layers.push(
        { id: 'pso-level0', label: 'Basic areas L0', category: MapLayerCategoryEnum.POLICE_AREAS, type: 'overlay' as const, visible: true, order: 50, layer: this.basicAreaLevel0LayerGroup },
        { id: 'pso-level1', label: 'Basic areas L1', category: MapLayerCategoryEnum.POLICE_AREAS, type: 'overlay' as const, visible: true, order: 51, layer: this.basicAreaLevel1LayerGroup },
        { id: 'pso-communications', label: 'Communications', category: MapLayerCategoryEnum.POLICE_AREAS, type: 'overlay' as const, visible: true, order: 52, layer: this.communicationsLayerGroup },
      );
    }

    // Debug layers — only for devel profile
    if (isDevel) {
      _layers.push(
        { id: 'debug-external-radius', label: 'External radius', category: MapLayerCategoryEnum.DEBUG, type: 'overlay' as const, visible: false, order: 60, layer: this.externalRadiusLayer, debugOnly: true },
        { id: 'debug-external-rectangle', label: 'External rectangle', category: MapLayerCategoryEnum.DEBUG, type: 'overlay' as const, visible: false, order: 61, layer: this.externalRectangleLayer, debugOnly: true },
        { id: 'debug-external-convex', label: 'External convex polygon', category: MapLayerCategoryEnum.DEBUG, type: 'overlay' as const, visible: false, order: 62, layer: this.externalConvexPolygonLayer, debugOnly: true },
        { id: 'debug-reduced-region', label: 'Reduced region approximation', category: MapLayerCategoryEnum.DEBUG, type: 'overlay' as const, visible: false, order: 63, layer: this.reducedRegionApproximationLayer, debugOnly: true },
        { id: 'debug-region-map', label: 'Region map', category: MapLayerCategoryEnum.DEBUG, type: 'overlay' as const, visible: false, order: 64, layer: this.regionMapLayer, debugOnly: true },
      );
    }

    this.layerRegistry.registerLayers(_layers);
    this.layerGroups = this.layerRegistry.getGroupedLayers();
  }

  /** Applies current registry visibility to the live Leaflet map. */
  private syncRegistryToMap(): void {
    if (!this.leafletmap) {
      return;
    }

    this.layerRegistry.syncToMap(this.leafletmap);
  }

  /** Keeps backward-compatible base layer switching while using the new registry. */
  switchBaseLayer(baseLayerName: L.TileLayer): void {
    this.selectedBaseLayer = baseLayerName;

    const entry = Object.entries(this.leafletBaseLayers).find(([, layer]) => layer === baseLayerName);
    if (!entry) {
      return;
    }

    const [name] = entry;
    const layerIdMap: Record<string, string> = {
      'Google Satellite': 'base-google-satellite',
      'Google Maps': 'base-google-maps',
      'Open Street Map': 'base-osm',
      'Arc GIS': 'base-arcgis',
    };

    this.layerRegistry.setBaseLayer(layerIdMap[name]);
  }

  /** Handles clicks on all map fabs and preserves the current UX. */
  onFabClick(fab: 'layers' | 'opacity' | 'location' | 'center' | 'restart' | 'windy'): void {
    this.activeFab = this.activeFab === fab ? 'none' : fab;

    if (fab !== 'location') {
      this.clearLocationSearch();
    }

    if (fab === 'layers') {
      this.showLayersPanel = !this.showLayersPanel;
      this.activeFab = this.showLayersPanel ? 'layers' : 'none';
      this.layersPanelChange.emit(this.showLayersPanel);
      return;
    }

    this.showLayersPanel = false;

    if (fab === 'center') {
      this.activeFab = 'none';
      this.centerSelection();
      return;
    }

    if (fab === 'restart') {
      this.activeFab = 'none';
      this.restartView();
      return;
    }

    if (fab === 'windy') {
      this.showWindyComponent();
    }
  }

  /** Handles custom panel overlay toggle changes. */
  onOverlayVisibilityChanged(event: { layerId: string; visible: boolean }): void {
    this.layerRegistry.setOverlayVisibility(event.layerId, event.visible);
  }

  /** Handles custom panel base layer changes. */
  onBaseLayerChanged(layerId: string): void {
    this.layerRegistry.setBaseLayer(layerId);

    const selected = {
      'base-google-satellite': this.leafletBaseLayers['Google Satellite'],
      'base-google-maps': this.leafletBaseLayers['Google Maps'],
      'base-osm': this.leafletBaseLayers['Open Street Map'],
      'base-arcgis': this.leafletBaseLayers['Arc GIS'],
    }[layerId];

    if (selected) {
      this.selectedBaseLayer = selected;
    }
  }

  /** Applies one of the custom panel presets. */
  onPresetSelected(preset: MapLayerPresetType): void {
    this.layerRegistry.applyPreset(preset);
  }

  /** Shows all overlays from the custom panel. */
  onShowAllLayers(): void {
    this.layerRegistry.showAllOverlays();
  }

  /** Hides all overlays from the custom panel. */
  onHideAllLayers(): void {
    this.layerRegistry.hideAllOverlays();
  }

  /** Resets overlays to the default preset. */
  onResetLayers(): void {
    this.layerRegistry.applyPreset('default');
  }

  /** Emits external center event preserved from the current contract. */
  centerSelection(): void {
    this.centerMap.emit();
  }

  /** Restarts the map view to the default center. */
  restartView(): void {
    if (!this.leafletmap) {
      return;
    }

    this.leafletmap.closePopup();
    this.leafletmap.flyTo(this.catCenter, 9, { animate: false, duration: 0.7 });
  }

  /** Changes the base layer opacity while keeping current behavior. */
  changeOpacity(opacityRange: any): void {
    if (!this.leafletmap) {
      return;
    }

    this.leafletmap.eachLayer((layer: any) => {
      if (layer?.options?.id === 'base' && layer.setOpacity) {
        layer.setOpacity(opacityRange.value);
      }
    });
  }

  /** Clears location search UI state. */
  clearLocationSearch(): void {
    this.locationSearchItems = [];
    this.locationSearchValue = '';
  }

  /** Executes manual location search as in the current component. */
  searchLocation(): void {
    if (!this.locationSearchValue) {
      return;
    }

    this.leafletMapService
      .searchLocation(this.locationSearchValue)
      .then((results: any[]) => {
        if (!results.length) {
          this.toastService.presentToast('The location could not be found.', 'bottom', 3000, '', 'danger', 'warning-outline');
          return;
        }
        this.locationSearchItems = results;
      })
      .catch(() => {
        this.toastService.presentToast('The location could not be found.', 'bottom', 3000, '', 'danger', 'warning-outline');
      });
  }

  /** Selects one search result and centers the map. */
  selectLocation(location: any): void {
    if (!this.leafletmap || !location) {
      return;
    }

    if (location.bounds && location.bounds.length === 4) {
      const southWest = L.latLng(location.bounds[0], location.bounds[1]);
      const northEast = L.latLng(location.bounds[2], location.bounds[3]);
      const bounds = L.latLngBounds(southWest, northEast);
      this.leafletmap.fitBounds(bounds, { padding: [50, 50], maxZoom: 18, animate: true });
    } else {
      this.leafletmap.setView([location.lat, location.lon], 12);
    }

    this.locationSearchItems = [];
  }

  /** Receives searchbar input and feeds the debounce stream. */
  onSearchInput(event: any): void {
    const term = event?.target?.value?.trim?.() ?? '';
    this.searchTerms.next(term);
  }

  /** Performs debounced location search. */
  private performLocationSearch(term: string): void {
    if (!term) {
      this.locationSearchItems = [];
      return;
    }

    this.leafletMapService
      .searchLocation(term)
      .then((results: any[]) => {
        if (!results.length) {
          this.toastService.presentToast('The location could not be found.', 'bottom', 3000, '', 'danger', 'warning-outline');
          return;
        }
        this.locationSearchItems = results;
      })
      .catch(() => {
        this.toastService.presentToast('The location could not be found.', 'bottom', 3000, '', 'danger', 'warning-outline');
      });
  }

  /** Opens the Windy modal preserving the current behavior. */
  async showWindyComponent(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: WindyComponent,
      cssClass: 'windyModal',
      componentProps: {
        latitude: this.catCenter.lat,
        longitude: this.catCenter.lng,
        selectedOp: false,
      },
    });

    await modal.present();
  }

  /** Captures the current map as a base64 PNG. */
  async captureMapAsBase64(): Promise<string> {
    return this.captureService.captureMapAsBase64(this.leafletmap, this.mapContainer?.nativeElement);
  }

  /** Returns whether a previous AP command is an exit point. */
  isExitPoint(prevCommand: any): boolean {
    return prevCommand?.apCommand === 'MissionArea';
  }

  /** Draws a polygon, including hole support, using the preserved behavior. */
  drawPolygon(
    region: any,
    layerGroup: L.LayerGroup,
    isProtectionArea: boolean = false,
    color?: string,
    tooltip?: string,
    weight: number = 2,
    fillOpacity: number = 0.4,
    pane?: string,
    dashArray?: string,
    hoverLabel?: string,
    interactive: boolean = true
  ): void {
    const polygonOptions: L.PolylineOptions = {
      fillOpacity,
      weight,
      interactive,
      bubblingMouseEvents: interactive,
    };
    let tooltipContent = tooltip ?? region?.description;

    if (color) {
      polygonOptions.color = color;
    }

    if (pane) {
      polygonOptions.pane = pane;
    }

    if (dashArray) {
      polygonOptions.dashArray = dashArray;
    }

    if (isProtectionArea) {
      const fromText = this.translateService.instant('FP.TOOLTIPS.FROM');
      const toText = this.translateService.instant('FP.TOOLTIPS.TO');
      tooltipContent = `${region?.description} ${fromText} ${region?.minAltitude}m, ${toText} ${region?.maxAltitude}m.`;
    }

    const polygonCoords: L.LatLngExpression[][] = [];

    if (region?.rset?.length > 0) {
      polygonCoords.push(region.rset.map((point: any) => new L.LatLng(point?.latitude, point?.longitude)));
    }

    if (region?.holeList?.length > 0) {
      region.holeList.forEach((hole: any[]) => {
        if (hole?.length) {
          polygonCoords.push(hole.map((point: any) => new L.LatLng(point?.latitude, point?.longitude)));
        }
      });
    }

    const polygon = hoverLabel
      ? L.polygon(polygonCoords, polygonOptions)
      : L.polygon(polygonCoords, polygonOptions).bindTooltip(tooltipContent, {
          permanent: false,
          direction: 'top',
          opacity: 0.8,
        });

    if (hoverLabel) {
      this.hoverService.registerPolygon(polygon as L.Polygon, {
        label: hoverLabel,
        description: region?.description,
        minAltitude: region?.minAltitude,
        maxAltitude: region?.maxAltitude,
        color: color ?? '#888',
      });
    } else if (interactive) {
      polygon.on('mouseover', () => polygon.openTooltip());
    }
    polygon.addTo(layerGroup);
  }

  /** Draws a circle using the preserved current behavior. */
  drawCircle(
    location: any,
    color: string,
    layerGroup: L.LayerGroup,
    dashArray?: string,
    isProtectionArea: boolean = false,
    pane?: string,
    fillOpacity: number = 0.1,
    hoverLabel?: string,
    interactive: boolean = true
  ): void {
    const locationCenter = L.latLng(location?.centre?.latitude, location?.centre?.longitude);
    let tooltipContent = location?.description;

    if (isProtectionArea) {
      const fromText = this.translateService.instant('FP.TOOLTIPS.FROM');
      const toText = this.translateService.instant('FP.TOOLTIPS.TO');
      const radiusText = this.translateService.instant('FP.TOOLTIPS.RADIUS');
      tooltipContent = `${location?.description} ${fromText} ${location?.minAltitude}m, ${toText} ${location?.maxAltitude}m. ${radiusText}: ${location?.radius}`;
    }

    if (location?.radius) {
      const circleOptions: any = {
        radius: location.radius,
        color,
        fillOpacity,
        dashArray,
        interactive,
        bubblingMouseEvents: interactive,
        ...(pane ? { pane } : {}),
      };
      const circle = hoverLabel
        ? L.circle(locationCenter, circleOptions)
        : L.circle(locationCenter, circleOptions).bindTooltip(tooltipContent, {
            permanent: false,
            direction: 'top',
            opacity: 0.8,
          });

      if (hoverLabel) {
        this.hoverService.registerCircle(circle as L.Circle, {
          label: hoverLabel,
          description: location?.description,
          minAltitude: location?.minAltitude,
          maxAltitude: location?.maxAltitude,
          color,
        });
      } else if (interactive) {
        circle.on('mouseover', () => circle.openTooltip());
      }
      circle.addTo(layerGroup);
    }
  }

  /** Draws one waypoint of an AP trajectory. */
  drawWaypoint(command: any, message?: string): void {
    const icon = this.icons.getIcon(
      command?.stageLabel + '-' + command?.apCommand
    );

    const marker = icon
      ? L.marker([command?.point?.latitude, command?.point?.longitude], { icon })
      : L.marker([command?.point?.latitude, command?.point?.longitude]);

    const altitudeText = this.translateService.instant('FP.INFO.ALTITUDE');

    marker.on('mouseover', () => {
      marker
        .bindTooltip(
          `${message ? message : command.label}<br>${altitudeText}: ${
            command?.point?.altitude
          }m`,
          {
            permanent: false,
            direction: 'top',
            opacity: 0.8,
          }
        )
        .openTooltip();
    });

    this.apTrajectoryLayerGroup.addLayer(marker);
  }

  /** Draws a segment between two AP commands. */
  drawSegment(fromCommand: any, toCommand: any): void {
    const color = this.colors.getColor(fromCommand.stageLabel);

    let segment: any = L.polyline(
      [
        [fromCommand?.point?.latitude, fromCommand?.point?.longitude],
        [toCommand?.point?.latitude, toCommand?.point?.longitude],
      ],
      { color }
    );

    if (typeof segment.arrowheads === 'function') {
      segment = segment.arrowheads({ size: '12px', frequency: 2 });
    }

    const speedText = this.translateService.instant('FP.INFO.SPEED');
    const altitudeText = this.translateService.instant('FP.INFO.ALTITUDE');

    segment.on('mouseover', () => {
      segment
        .bindTooltip(
          `${speedText}: ${fromCommand?.speed}<br>${altitudeText}: ${fromCommand?.point?.altitude}m`,
          {
            permanent: false,
            direction: 'top',
            opacity: 0.8,
          }
        )
        .openTooltip();
    });

    this.apTrajectoryLayerGroup.addLayer(segment);
  }

  /** Draws a mission area polygon. */
  drawMissionArea(command: any): void {
    const color = this.colors.getColor(command?.stageLabel);

    if (command?.region?.rset?.length > 0) {
      const polygon = L.polygon(
        command.region.rset.map(
          (point: any) => new L.LatLng(point?.latitude, point?.longitude)
        ),
        { color }
      );

      const minAltText = this.translateService.instant('FP.INFO.MIN_ALT');
      const maxAltText = this.translateService.instant('FP.INFO.MAX_ALT');

      polygon.on('mouseover', () => {
        polygon
          .bindTooltip(
            `${command?.label}<br>${minAltText}: ${command?.region?.minAltitude}m.<br>${maxAltText}: ${command?.region?.maxAltitude}m.`,
            { permanent: false, direction: 'top', opacity: 0.8 }
          )
          .openTooltip();
      });

      this.apTrajectoryLayerGroup.addLayer(polygon);
    }
  }

  /** Delegates relay vehicle drawing to the extracted relay service. */
  drawVehiclesRelay(lastMessages: any[]): void {
    this.relayService.upsertVehicle({
      map: this.leafletmap,
      messages: lastMessages,
      tooltipFactory: this.tooltipFactory,
      injector: this.injector,
      createColoredDroneIcon: createColoredDroneIcon,
    });
  }

  /** Clears all domain layer groups without affecting base maps. */
  clearAllLayers(): void {
    Object.values(this.mapLayers).forEach((categoryLayers: any) => {
      categoryLayers.forEach((layer: L.LayerGroup) => layer.clearLayers());
    });
  }

  /** Clears infrastructure groups only. */
  clearInfrastructureLayers(): void {
    this.mapLayers.infrastructures.forEach((layer: L.LayerGroup) => layer.clearLayers());
    this.hoverService.clearShapes();
  }

  /** Clears PSO basic area groups only. */
  clearBasicAreasLayers(): void {
    this.basicAreaLevel0LayerGroup.clearLayers();
    this.basicAreaLevel1LayerGroup.clearLayers();
  }

  /** Clears operation-related groups only. */
  clearOperationLayers(): void {
    this.mapLayers.operations.forEach((layer: L.LayerGroup) => layer.clearLayers());
    this.operationMarkerMap.forEach((marker) => marker?.remove());
    this.operationMarkerMap.clear();
  }

  /** Clears operation markers only. */
  clearAllOperationLocations(): void {
    this.operationMarkerMap.forEach((operationMarker) => {
      if (this.leafletmap?.hasLayer(operationMarker)) {
        this.leafletmap.removeLayer(operationMarker);
      }
    });
    this.operationMarkerMap.clear();
  }

  /** Clears coordination conflicts only. */
  clearCoordinationConflicts(): void {
    this.coordinationConflictLayer.clearLayers();
  }

  /** Clears autopilot trajectories only. */
  clearApTrajectories(): void {
    this.apTrajectoryLayerGroup.eachLayer((layer: any) => {
      try {
        /**
         * Some leaflet-arrowheads plugin versions attach extra internal layers.
         * If the plugin provides a cleanup method, call it first.
         */
        if (typeof layer.deleteArrowheads === 'function') {
          layer.deleteArrowheads();
        }

        this.apTrajectoryLayerGroup.removeLayer(layer);
      } catch (error) {
        console.warn('Error removing AP trajectory layer:', error);
      }
    });
  }

  /** Clears one relay vehicle. */
  clearMapRelayVehicles(id: string): void {
    this.relayService.clearVehicle(this.leafletmap, id);
  }

  public isRelayVehicleVisible(id: string): boolean {
    return this.relayService.isVehicleVisible(id);
  }

  /** Draws infrastructures using the preserved routing logic. */
  drawInfrastructures(
    infrastructures: any[],
    isDetailPage: boolean = false,
    markerLayerGroup: L.LayerGroup | L.Map = this.markersLayerGroup,
    interactive: boolean = true
  ): L.Marker[] {
    const markers: L.Marker[] = [];

    if (!infrastructures?.length) {
      return markers;
    }

    infrastructures.forEach((infra) => {
      if (infra?.location) {
        const marker = this.drawInfrastructureMarker(
          infra,
          isDetailPage,
          markerLayerGroup,
          this.pageStatusService.isInfraestructureOperatorHomePage(),
          !isDetailPage,
          interactive
        );

        if (marker) {
          markers.push(marker);
        }
      }

      if (infra?.regionMap?.regions?.length > 0) {
        this.drawInfrastructureRegion(infra, infra?.code ?? infra?.infrastructureName ?? '', interactive);
      }

      if (infra?.regionMap?.locations?.length > 0) {
        this.drawInfrastructureLocation(infra, infra?.code ?? infra?.infrastructureName ?? '', interactive);
      }

      if (infra?.regionMap?.tracks?.length > 0) {
        this.drawInfrastructureTrack(infra, interactive);
      }
    });

    return markers;
  }

  /** Draws track networks. */
  drawTrackNetworks(trackNetworks: any[], isDetailPage: boolean = false): void {
    if (!trackNetworks?.length) {
      return;
    }

    trackNetworks.forEach((trackNetwork) => {
      if (trackNetwork?.location) {
        this.drawTrackNetworkMarker(trackNetwork, isDetailPage);
      }

      if (isDetailPage && trackNetwork?.regionApproximation?.externalRectangle?.pointsClockwise?.length > 0) {
        this.drawExternalRectangle(
          trackNetwork.regionApproximation.externalRectangle,
          this.polygonLayer,
          'aqua',
          this.translateService.instant('FP.INFO.EXTERNAL_RECTANGLE'),
          2,
          0.05
        );
      }
    });
  }

  /** Draws an external rectangle polygon. */
  drawExternalRectangle(
    externalRectangle: any,
    layerGroup: L.LayerGroup,
    color?: string,
    tooltip?: string,
    weight: number = 2,
    fillOpacity: number = 0.4
  ): void {
    const polygonOptions: L.PolylineOptions = { fillOpacity, weight };
    if (color) {
      polygonOptions.color = color;
    }

    const rectangleCoords: L.LatLngExpression[] = externalRectangle.pointsClockwise.map(
      (point: any) => new L.LatLng(point.latitude, point.longitude)
    );

    const polygon = L.polygon(rectangleCoords, polygonOptions).bindTooltip(tooltip, {
      permanent: false,
      direction: 'top',
      opacity: 0.8,
    });

    polygon.on('mouseover', () => polygon.openTooltip());
    polygon.addTo(layerGroup);
  }

  /** Draws one infrastructure marker. */
  drawInfrastructureMarker(
    infra: any,
    isDetailPage: boolean,
    targetLayerGroup: L.LayerGroup | L.Map = this.markersLayerGroup,
    enablePopup: boolean = true,
    centerOnClick: boolean = !isDetailPage,
    interactive: boolean = true
  ): L.Marker | undefined {
    try {
      const { values } = infra?.location?.coordinate;
      const infraPosition = L.latLng(values[1], values[0]);

      const icon = icons.getInfrastructureIcon(
        infra?.type,
        infra?.manualCoordination
      );

      const infraMarker = icon
        ? L.marker(infraPosition, { icon, interactive, bubblingMouseEvents: interactive, keyboard: interactive })
        : L.marker(infraPosition, { interactive, bubblingMouseEvents: interactive, keyboard: interactive });

      if (interactive) {
        infraMarker.bindTooltip(infra?.code + ' - ' + infra?.infrastructureName, {
          permanent: false,
          direction: 'top',
          opacity: 0.8,
        });
      }

      if (interactive && enablePopup && this.pageStatusService.isInfraestructureOperatorHomePage()) {
        infraMarker
          .bindPopup(this.obtainInfrastructurePopUp(infra), {
            className: 'customPopup',
          })
          .on('popupopen', (a: any) => {
            const popUp = a.target.getPopup();
            const button = popUp?.getElement()?.querySelector('.infraDetail');
            if (button) {
              button.addEventListener('click', () => {
                this.router.navigate([`/infrastructure/${infra?.uniqueId}`]);
              });
            }
          });
      }

      if (interactive) {
        infraMarker.on('click', () => {
          if (centerOnClick) {
            this.centerByRadius(infraPosition, 500);
          }
        });
      }

      infraMarker.addTo(targetLayerGroup);
      return infraMarker;
    } catch (error) {
      console.error('Error drawing marker infrastructure:', error);
      return undefined;
    }
  }

  /** Draws one track network marker. */
  drawTrackNetworkMarker(
    trackNetwork: any,
    isDetailPage: boolean = false,
    targetLayerGroup: L.LayerGroup | L.Map = this.markersLayerGroup,
    enablePopup: boolean = true,
    centerOnClick: boolean = !isDetailPage,
    interactive: boolean = true
  ): L.Marker | undefined {
    try {
      const { values } = trackNetwork?.location?.coordinate;
      const position = L.latLng(values[1], values[0]);

      const icon = icons.RailWayIcon;
      const marker = icon
        ? L.marker(position, { icon, interactive, bubblingMouseEvents: interactive, keyboard: interactive })
        : L.marker(position, { interactive, bubblingMouseEvents: interactive, keyboard: interactive });

      if (interactive) {
        marker.on('mouseover', () => {
          marker
            .bindTooltip(trackNetwork?.name + ' - ' + trackNetwork?.netName, {
              permanent: false,
              direction: 'top',
              opacity: 0.8,
            })
            .openTooltip();
        });
      }

      if (interactive && enablePopup && this.pageStatusService.isInfraestructureOperatorHomePage()) {
        marker
          .bindPopup(this.obtainTrackNetworkPopUp(trackNetwork), {
            className: 'customPopup',
          })
          .on('popupopen', (a: any) => {
            const popUp = a.target.getPopup();
            const button = popUp?.getElement()?.querySelector(
              '.trackNetworkDetail'
            );
            if (button) {
              button.addEventListener('click', () => {
                this.router.navigate(
                  [`/infrastructure/${trackNetwork?.infrastructureUuid}`],
                  {
                    state: { trackNetworkId: trackNetwork?.uniqueId },
                  }
                );
              });
            }
          });
      }

      if (interactive) {
        marker.on('click', () => {
          if (centerOnClick) {
            this.centerByRadius(position, 500);
          }
        });
      }

      marker.addTo(targetLayerGroup);
      return marker;
    } catch (error) {
      console.error('Error drawing marker track network:', error);
      return undefined;
    }
  }

  /** Draws infrastructure region layers. */
  drawInfrastructureRegion(infra: any, infraName: string = '', interactive: boolean = true): void {
    const typeKey = infraTypeKey(infra?.type ?? '');
    infra?.regionMap?.regions?.forEach((regionRef: any) => {
      try {
        const region = regionRef.region;
        region.minAltitude = regionRef.minAltitude ?? region.minAltitude;
        region.maxAltitude = regionRef.maxAltitude ?? region.maxAltitude;
        region.description = regionRef.description ?? region.description;
        const regionName = (
          regionRef?.regionName ?? region?.name ?? ''
        )
          .toLowerCase?.() ?? '';
        const prefix = infraName ? `${infraName} ` : '';

        if (regionName.includes('structure')) {
          const s = MAP_STYLES.structure;
          this.drawPolygon(region, this.structuresLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, s.dash, `${prefix}Structure`, interactive);
        } else if (regionName.includes('ctr') || regionName.includes('atz')) {
          const isCtr = regionName.includes('ctr');
          const s = isCtr ? MAP_STYLES.ctr : MAP_STYLES.atz;
          this.drawPolygon(region, this.ctrAtzLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, s.dash, `${prefix}${isCtr ? 'CTR' : 'ATZ'}`, interactive);
        } else if (regionName.includes('protection')) {
          if (regionName.includes('high')) {
            const s = MAP_STYLES.protectionHigh[typeKey];
            this.drawPolygon(region, this.protectionHighLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, s.dash, `${prefix}Protection High`, interactive);
          } else if (regionName.includes('low')) {
            const s = MAP_STYLES.protectionLow[typeKey];
            this.drawPolygon(region, this.protectionLowLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, s.dash, `${prefix}Protection Low`, interactive);
          } else if (regionName.includes('park')) {
            const s = MAP_STYLES.naturalPark;
            this.drawPolygon(region, this.naturalParksLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, null, `${prefix}Natural park`, interactive);
          } else if (regionName.includes('harbour')) {
            const s = MAP_STYLES.harbour;
            this.drawPolygon(region, this.harboursLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, null, `${prefix}Harbour`, interactive);
          } else {
            const s = MAP_STYLES.other;
            this.drawPolygon(region, this.otherLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, s.dash, `${prefix}Restricted area`, interactive);
          }
        } else if (regionName.includes('subarea')) {
          const s = MAP_STYLES.innerZone;
          this.drawPolygon(region, this.innerZonesLayerGroup, true, s.color, undefined, s.weight, s.fillOpacity, s.pane, s.dash, `${prefix}Inner zone`, interactive);
        }
      } catch (error) {
        console.error('Error drawing region infrastructure:', error);
      }
    });
  }

  /** Draws infrastructure locations such as protection circles and helipads. */
  drawInfrastructureLocation(infra: any, infraName: string = '', interactive: boolean = true): void {
    const typeKey = infraTypeKey(infra?.type ?? '');
    const prefix = infraName ? `${infraName} ` : '';
    const allLocationRefs: any[] = infra?.regionMap?.locations ?? [];
    allLocationRefs.forEach((locationRef: any) => {
      try {
        // Some entries borrow geometry from a referenced entry (referenceLocationName).
        const location = locationRef.location
          ?? allLocationRefs.find(
              (r: any) => r.locationName === locationRef.referenceLocationName && r.location
            )?.location;
        if (!location) {
          return;
        }
        location.radius = locationRef.radius ?? location.radius;
        location.minAltitude = locationRef.minAltitude ?? location.minAltitude;
        location.maxAltitude = locationRef.maxAltitude ?? location.maxAltitude;
        location.description = locationRef.description ?? location.description;

        const locationName = (
          locationRef.locationName ?? location.name ?? ''
        ).toLowerCase();
        if (locationName.includes('protection')) {
          if (locationName.includes('high')) {
            const s = MAP_STYLES.protectionHigh[typeKey];
            this.drawCircle(location, s.color, this.protectionHighLayerGroup, s.dash, true, s.pane, s.fillOpacity, `${prefix}Protection High`, interactive);
          } else if (locationName.includes('low')) {
            const s = MAP_STYLES.protectionLow[typeKey];
            this.drawCircle(location, s.color, this.protectionLowLayerGroup, s.dash, true, s.pane, s.fillOpacity, `${prefix}Protection Low`, interactive);
          } else {
            const s = MAP_STYLES.other;
            this.drawCircle(location, s.color, this.otherLayerGroup, s.dash, true, s.pane, s.fillOpacity, `${prefix}Restricted area`, interactive);
          }
        } else if (locationName.includes('helipad')) {
          const s = MAP_STYLES.helipad;
          this.drawCircle(location, s.color, this.runwayLayerGroup, null, false, s.pane, s.fillOpacity, undefined, interactive);
        }
      } catch (error) {
        console.error('Error drawing location infrastructure:', error);
      }
    });
  }

  /** Draws urban areas into two separate layer groups: one for population areas and one for land registry (RMAP) areas. */
  drawUrbanAreas(
    urbanAreas: any[],
    populationLayerGroup: L.LayerGroup = this.urbanAreasLayerGroup,
    registryLayerGroup: L.LayerGroup = this.urbanAreasRegistryLayerGroup,
    interactive: boolean = true
  ): void {
    if (!urbanAreas?.length) {
      return;
    }

    urbanAreas.forEach((urbanArea) => {
      const isRegistry = (urbanArea?.region?.name as string)?.includes('RMAP');
      const s = isRegistry ? MAP_STYLES.urbanAreaRegistry : MAP_STYLES.urbanAreaPopulation;
      const targetLayerGroup = isRegistry ? registryLayerGroup : populationLayerGroup;

      if (urbanArea?.regionMap?.regions?.length) {
        urbanArea.regionMap.regions.forEach((regionRef: any) => {
          const region = regionRef.region;
          const tooltip = `${urbanArea?.regionMapName} - ${urbanArea?.organizationShortName}`;
          try {
            this.drawPolygon(region, targetLayerGroup, false, s.color, tooltip, s.weight, s.fillOpacity, s.pane, undefined, undefined, interactive);
          } catch (error) {
            console.error('Error drawing urban area region:', error);
          }
        });
      } else if (urbanArea?.region) {
        try {
          const tooltip = `${urbanArea?.regionName} - ${urbanArea?.organizationShortName}`;
          this.drawPolygon(urbanArea.region, targetLayerGroup, false, s.color, tooltip, s.weight, s.fillOpacity, s.pane, undefined, undefined, interactive);
        } catch (error) {
          console.error('Error drawing urban area region:', error);
        }
      }
    });
  }

  /** Draws track segments into the provided line and marker layer groups. */
  drawTrackSegments(
    trackSegments: any[],
    lineLayerGroup: L.LayerGroup = this.trackNetworkLayerGroup,
    markerLayerGroup: L.LayerGroup | L.Map = this.markersLayerGroup,
    interactive: boolean = true
  ): L.Marker[] {
    const trackColors = MAP_STYLES.trackPalette;
    const markers: L.Marker[] = [];

    if (!trackSegments?.length) {
      return markers;
    }

    trackSegments.forEach((trackSegment: any, index: number) => {
      try {
        if (trackSegment?.location) {
          const marker = this.drawTrackNetworkMarker(
            trackSegment,
            false,
            markerLayerGroup,
            false,
            false,
            interactive
          );

          if (marker) {
            markers.push(marker);
          }
        }

        if (trackSegment?.trackSeq?.tsequence?.length > 0) {
          const segmentCoords: L.LatLngExpression[] = trackSegment.trackSeq.tsequence.map(
            (point: any) => new L.LatLng(point.latitude, point.longitude)
          );

          const trackColor = trackColors[index % trackColors.length];
          const trackLine = L.polyline(segmentCoords, {
            color: trackColor,
            weight: MAP_STYLES.trackWeight,
            pane: MAP_STYLES.trackPane,
            interactive,
            bubblingMouseEvents: interactive,
          });

          let tooltipContent = '';
          if (trackSegment?.segmentCode) {
            tooltipContent += `<strong>${trackSegment.segmentCode}</strong>`;
          }
          if (trackSegment?.description) {
            tooltipContent += tooltipContent
              ? `<br>${trackSegment.description}`
              : trackSegment.description;
          }
          if (trackSegment?.originName || trackSegment?.destinationName) {
            const route = `${trackSegment.originName || '?'} → ${trackSegment.destinationName || '?'}`;
            tooltipContent += tooltipContent ? `<br>${route}` : route;
          }

          if (tooltipContent) {
            trackLine.bindTooltip(tooltipContent, {
              permanent: false,
              direction: 'top',
              opacity: 0.8,
              className: 'track-tooltip',
            });

            if (interactive) {
              trackLine.on('mouseover', () => {
                trackLine.openTooltip();
              });
            }
          }

          lineLayerGroup.addLayer(trackLine);
        }
      } catch (error) {
        console.error('Error drawing track segment infrastructure:', error);
      }
    });

    return markers;
  }

  /** Draws infrastructure tracks. */
  drawInfrastructureTrack(infra: any, interactive: boolean = true): void {
    const trackColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'];

    infra?.regionMap?.tracks?.forEach((trackRef: any, index: number) => {
      try {
        const track = trackRef.trackSequence;
        track.description = trackRef.description;
        track.name = trackRef.trackName;
        track.minAltitude = trackRef.minAltitude;
        track.maxAltitude = trackRef.maxAltitude;

        const segmentCoords: L.LatLngExpression[] = track.tsequence.map((point: any) => new L.LatLng(point.latitude, point.longitude));
        const trackColor = trackColors[index % trackColors.length];
        const trackLine = L.polyline(segmentCoords, {
          color: trackColor,
          weight: 3,
          pane: 'pane-harbours-tracks',
          interactive,
          bubblingMouseEvents: interactive,
        });

        let tooltipContent = '';
        if (track.name) {
          tooltipContent += `<strong>${track.name}</strong>`;
        }
        if (track.description) {
          tooltipContent += tooltipContent ? `<br>${track.description}` : track.description;
        }

        if (tooltipContent) {
          trackLine.bindTooltip(tooltipContent, {
            permanent: false,
            direction: 'top',
            opacity: 0.8,
            className: 'track-tooltip',
          });
          if (interactive) {
            trackLine.on('mouseover', () => trackLine.openTooltip());
          }
        }

        this.trackNetworkLayerGroup.addLayer(trackLine);
      } catch (error) {
        console.error('Error drawing track infrastructure:', error);
      }
    });
  }

  /** Creates infrastructure popup HTML. */
  obtainInfrastructurePopUp(infrastructure: any): string {
    const { code, infrastructureName } = infrastructure;
    return `
      <div>
        <strong>${infrastructureName}</strong><br>
        ${this.translateService.instant('INFRASTRUCTURES.CODE')}: ${code}<br>
        <button style="font-weight:bold; color: #003083" class="infraDetail">${this.translateService.instant('INFRASTRUCTURES.GO_TO_INFRA')}</button>
      </div>
    `;
  }

  /** Creates track network popup HTML. */
  obtainTrackNetworkPopUp(trackNetwork: any): string {
    const { netName, name } = trackNetwork;
    return `
      <div>
        <strong>${name}</strong><br>
        ${this.translateService.instant('TRACKNETWORKS.NETWORK_NAME')}: ${netName}<br>
        <button style="font-weight:bold; color: #003083" class="trackNetworkDetail">${this.translateService.instant('INFRASTRUCTURES.GO_TO_INFRA')}</button>
      </div>
    `;
  }

  /** Draws all basic areas using the current preserved selection behavior. */
  drawBasicAreas(basicAreaList: any[], selectedLevel0Id?: string, selectedLevel1Id?: string, communicationSelected: boolean = false): void {
    this.clearLayer(this.basicAreaLevel0LayerGroup);
    this.clearLayer(this.selectedBasicAreaLevel0Layer);
    this.clearLayer(this.basicAreaLevel1LayerGroup);
    this.clearLayer(this.selectedBasicAreaLevel1Layer);

    const level0Areas = basicAreaList.filter((a) => a.areaLevel === 0);
    level0Areas.forEach((area) => {
      this.drawSelectedBasicAreaRegion(area, this.basicAreaLevel0LayerGroup, false, selectedLevel0Id, selectedLevel1Id, communicationSelected);
    });

    if (selectedLevel0Id) {
      this.setLayerOpacity(this.basicAreaLevel0LayerGroup, 0.2);

      const selectedArea = level0Areas.find((a) => a.uniqueId === selectedLevel0Id);
      if (selectedArea) {
        this.drawSelectedBasicAreaRegion(selectedArea, this.selectedBasicAreaLevel0Layer, true, selectedLevel0Id, selectedLevel1Id, communicationSelected);
      }

      const level1Areas = basicAreaList.filter((a) => a.areaLevel === 1 && a.referenceAreaUuid === selectedLevel0Id);
      level1Areas.forEach((subarea) => {
        this.drawSelectedBasicAreaRegion(subarea, this.basicAreaLevel1LayerGroup, false, selectedLevel0Id, selectedLevel1Id, communicationSelected);
      });

      if (selectedLevel1Id) {
        this.setLayerOpacity(this.basicAreaLevel1LayerGroup, 0.4);
        const selectedSubarea = level1Areas.find((a) => a.uniqueId === selectedLevel1Id);
        if (selectedSubarea) {
          this.drawSelectedBasicAreaRegion(selectedSubarea, this.selectedBasicAreaLevel1Layer, true, selectedLevel0Id, selectedLevel1Id, communicationSelected);
        }
      } else {
        this.setLayerOpacity(this.basicAreaLevel1LayerGroup, 0.4);
      }
    }
  }

  /** Draws affected basic areas for one communication. */
  drawAffectedAreas(allAreas: any[], level0Ids: string[], level1Ids: string[], communicationSelected: boolean = false): void {
    this.clearLayer(this.basicAreaLevel0LayerGroup);
    this.clearLayer(this.selectedBasicAreaLevel0Layer);
    this.clearLayer(this.basicAreaLevel1LayerGroup);
    this.clearLayer(this.selectedBasicAreaLevel1Layer);

    const level0Areas = allAreas.filter((a) => a.areaLevel === 0 && !level0Ids.includes(a.uniqueId));
    level0Areas.forEach((area) => {
      this.drawSelectedBasicAreaRegion(area, this.basicAreaLevel0LayerGroup, false, null, null, communicationSelected);
    });

    const highlightedLevel0Areas = allAreas.filter((a) => a.areaLevel === 0 && level0Ids.includes(a.uniqueId));
    highlightedLevel0Areas.forEach((area) => {
      this.drawSelectedBasicAreaRegion(area, this.selectedBasicAreaLevel0Layer, true, null, null, communicationSelected);
    });

    const level1Areas = allAreas.filter((a) => a.areaLevel === 1 && level0Ids.includes(a.referenceAreaUuid) && !level1Ids.includes(a.uniqueId));
    level1Areas.forEach((area) => {
      this.drawSelectedBasicAreaRegion(area, this.basicAreaLevel1LayerGroup, false, area.referenceAreaUuid, null, communicationSelected);
    });

    const highlightedLevel1Areas = allAreas.filter((a) => a.areaLevel === 1 && level1Ids.includes(a.uniqueId));
    highlightedLevel1Areas.forEach((area) => {
      this.drawSelectedBasicAreaRegion(area, this.selectedBasicAreaLevel1Layer, true, area.referenceAreaUuid, area.uniqueId, communicationSelected);
    });

    this.setLayerOpacity(this.basicAreaLevel0LayerGroup, 0.2);
    this.setLayerOpacity(this.selectedBasicAreaLevel0Layer, 0.5);
    this.setLayerOpacity(this.basicAreaLevel1LayerGroup, 0.3);
    this.setLayerOpacity(this.selectedBasicAreaLevel1Layer, 0.6);
  }

  /** Draws one selected or unselected basic area polygon. */
  drawSelectedBasicAreaRegion(
    basicArea: any,
    layerToUse: L.LayerGroup,
    isSelected: boolean = false,
    selectedLevel0Id?: string,
    selectedLevel1Id?: string,
    communicationSelected: boolean = false
  ): void {
    const tooltipContent = basicArea?.areaName;
    const fillOpacity = isSelected ? 0.5 : 0.3;

    // Collect the Region objects to paint, trying sources in order of detail.
    let regions: any[] = [];

    if (basicArea?.regionMap?.regions?.length > 0) {
      // Full regionMap populated — extract region from each RegionRef
      regions = basicArea.regionMap.regions.map((ref: any) => ref.region).filter(Boolean);
    } else if (basicArea?.regionApproximation?.reducedRegionMap?.length > 0) {
      // No full regionMap — fall back to reduced region map (array of Region)
      regions = basicArea.regionApproximation.reducedRegionMap.filter(Boolean);
    } else if (basicArea?.regionApproximation?.externalConvexPolygon?.rset?.length > 0) {
      // Last resort: convex hull approximation
      regions = [basicArea.regionApproximation.externalConvexPolygon];
    }

    if (regions.length === 0) {
      console.warn(`[LeafletMap] drawSelectedBasicAreaRegion: no geometry for areaName=${basicArea?.areaName}`);
      return;
    }

    let allowHighlight = !communicationSelected;
    if (basicArea.areaLevel === 0 && selectedLevel0Id) {
      allowHighlight = isSelected && !communicationSelected;
    }
    if (basicArea.areaLevel === 1 && selectedLevel1Id) {
      allowHighlight = isSelected && !communicationSelected;
    }

    regions.forEach((region: any) => {
      try {
        const polygonOptions: L.PolylineOptions = {
          fillOpacity,
          weight: 3,
          color: isSelected ? '#000000' : '#808080',
          fillColor: basicArea.regionColor,
        };

        const polygon = L.polygon(
          region.rset.map((point: any) => new L.LatLng(point.latitude, point.longitude)),
          polygonOptions
        ).bindTooltip(tooltipContent, {
          permanent: false,
          direction: 'top',
          opacity: 0.8,
        });

        if (!isSelected && allowHighlight) {
          polygon.on('mouseover', () => {
            polygon.openTooltip();
            polygon.setStyle({ color: '#000000', fillOpacity: fillOpacity + 0.2 });
            (polygon as any).bringToFront?.();
          });

          polygon.on('mouseout', () => {
            let currentOpacity = fillOpacity;
            if (basicArea.areaLevel === 1 && selectedLevel0Id && !selectedLevel1Id) {
              currentOpacity = 0.4;
            }
            polygon.setStyle({ color: '#808080', fillOpacity: currentOpacity });
          });
        } else {
          polygon.on('mouseover', () => polygon.openTooltip());
        }

        if (basicArea.uniqueId) {
          polygon.on('click', () => {
            if (!communicationSelected) {
              this.basicAreaClicked.emit(basicArea.uniqueId);
            }
          });
        }

        polygon.addTo(layerToUse);
      } catch (error) {
        console.error('Error drawing region:', error);
      }
    });
  }

  /** Sets fill opacity for all stylable layers inside a group. */
  setLayerOpacity(layerGroup: L.LayerGroup, opacity: number): void {
    layerGroup.eachLayer((layer: any) => {
      if (layer?.setStyle) {
        layer.setStyle({ fillOpacity: opacity });
      }
    });
  }

  /** Clears one specific layer group. */
  clearLayer(layerGroup: L.LayerGroup): void {
    layerGroup.clearLayers();
  }

  drawPSCommunicationsLocation(comm: any, closestDate?: string): void {
    this.drawCommunicationMarker(
      comm,
      createDroneIcon('var(--color-ext-maizecrayola)', 30, '#000000', 4),
      closestDate
    );
  }

  drawBACommunicationsLocation(comm: any, closestDate?: string): void {
    this.drawCommunicationMarker(
      comm,
      createDroneIcon('var(--ion-color-danger)', 30, '#000000', 4),
      closestDate
    );
  }

  drawOperationLocation(opRequest: any): void {
    if (opRequest?.centroid) {
      const iconColor =
        OperationStatusColorCodeMap[opRequest?.operationStatus] ??
        'var(--ion-color-medium)';

      const marker = L.marker(
        new L.LatLng(
          opRequest?.centroid?.latitude,
          opRequest?.centroid?.longitude
        ),
        {
          icon: createDroneIcon(iconColor, 30, '#000000', 4),
        }
      )
        .bindTooltip(opRequest?.callSign, {
          permanent: false,
          direction: 'top',
          opacity: 0.8,
        })
        .on('mouseover', () => marker.openTooltip())
        .on('click', () => this.operationSelected.emit(opRequest?.uniqueId));

      this.apOperationLayerGroup.addLayer(marker);
      this.operationMarkerMap.set(opRequest?.uniqueId, marker);
    }
  }

  updateOperationStatus(uniqueId: string, status: 'active' | 'warning' | 'error'): void {
    const marker = this.operationMarkerMap.get(uniqueId);
    if (marker) {
      const colorMap = {
        active: 'var(--ion-color-success)',
        warning: 'var(--ion-color-warning)',
        error: 'var(--ion-color-danger)',
      };
      updateDroneIconColor(marker, colorMap[status]);
    }
  }

  /** Shared communication marker renderer. */
  private drawCommunicationMarker(
    comm: any,
    icon: L.Icon | L.DivIcon,
    closestDate?: string
  ): void {
    if (!comm?.centroid) {
      return;
    }

    if (this.communicationMarkers.has(comm.uniqueId)) {
      this.communicationsLayerGroup.removeLayer(this.communicationMarkers.get(comm.uniqueId));
      this.communicationMarkers.delete(comm.uniqueId);
    }

    const marker = L.marker(new L.LatLng(comm?.centroid?.latitude, comm?.centroid?.longitude), { icon });

    marker.on('mouseover', () => {
      marker.bindTooltip(this.obtainCommunicationPopUp(comm, closestDate), {
        permanent: false,
        direction: 'top',
        opacity: 0.8,
        className: 'customPopup',
      }).openTooltip();
    });

    marker.on('click', () => {
      this.setSelectedCommunication(comm.uniqueId);
      this.communicationSelected.emit(comm);
      this.leafletmap?.setView([comm?.centroid?.latitude, comm?.centroid?.longitude], 15);
    });

    marker.addTo(this.communicationsLayerGroup);
    this.communicationMarkers.set(comm.uniqueId, marker);
  }

  /** Sets the selected communication marker color. */
  setSelectedCommunication(commId: string | null): void {
    if (this.selectedCommunicationId && this.communicationMarkers.has(this.selectedCommunicationId)) {
      const prevMarker = this.communicationMarkers.get(this.selectedCommunicationId);
      prevMarker.setIcon(createDroneIcon('var(--color-ext-maizecrayola)', 30, '#000000', 4));
    }

    if (commId && this.communicationMarkers.has(commId)) {
      const marker = this.communicationMarkers.get(commId);
      marker.setIcon(createDroneIcon('var(--color-ext-yelloworange)', 30, '#000000', 4));
      marker.setZIndexOffset(1000);
    }

    this.selectedCommunicationId = commId;
  }

  /** Clears communication selection. */
  clearSelectedCommunication(): void {
    this.setSelectedCommunication(null);
  }

  /** Clears all communication markers. */
  clearCommunicationsLocations(): void {
    this.communicationMarkers.clear();
    this.communicationsLayerGroup.clearLayers();
  }

  /** Creates communication popup HTML. */
  obtainCommunicationPopUp(comm: any, closestDate?: string): string {
    let popUpContent = `
      <div>
        <strong>${this.translateService.instant('COMMON.LABELS.COMMUNICATION_NUMBER')}: ${comm.communicationNumber}<strong><br>
        <strong>${comm.droneOperatorName}</strong><br>
        ${this.translateService.instant('OPREQUEST.CALLSIGN')}: ${comm.operationCallsign}<br>
    `;

    if (closestDate) {
      const finishedMsg = this.translateService.instant('COMMON.LABELS.FINISHED_OPERATION');
      const todayStr = new Date().toISOString().split('T')[0];
      if (closestDate === 'past') {
        popUpContent += `<span style="color:red"><strong>${finishedMsg}</strong></span><br>`;
      } else {
        const [year, month, day] = closestDate.split('-');
        const displayDate = `${day}-${month}-${year}`;
        if (closestDate === todayStr) {
          popUpContent += `${this.translateService.instant('COMMON.LABELS.NEXT_FLIGHT_DAY')}: <strong style="color:green">${this.translateService.instant('COMMON.LABELS.TODAY')}</strong><br>`;
        } else {
          popUpContent += `${this.translateService.instant('COMMON.LABELS.NEXT_FLIGHT_DAY')}: <strong>${displayDate}</strong><br>`;
        }
      }
    }

    popUpContent += `</div>`;
    return popUpContent;
  }

  /** Draws one named operation region. */
  drawOperationArea(regions: any[], regionToDraw: string): void {
    const polygonOptions: L.PolylineOptions = { fillOpacity: 0.2, weight: 2, color: 'pink' };

    regions?.forEach((region: any) => {
      if (region?.name === regionToDraw) {
        const polygon = L.polygon(
          region.rset.map((point: any) => new L.LatLng(point?.latitude, point?.longitude)),
          polygonOptions
        );
        this.polygonLayer.addLayer(polygon);
        this.polygonLayer.setZIndex(100);
      }
    });
  }

  /** Creates altitude-conflict popup HTML. */
  obtainConflictsPopUp(altitudeOrigin: number, altitudeDestination: number): string {
    if (altitudeOrigin !== altitudeDestination) {
      return `
        <div>
          <strong>${this.translateService.instant('COMMON.LABELS.ALTITUDE_CONFLICT_BETWEEN')} ${altitudeOrigin}${this.translateService.instant('COMMON.LABELS.M_AND')}m and ${altitudeDestination}${this.translateService.instant('COMMON.LABELS.M')}</strong><br>
        </div>
      `;
    }

    return `
      <div>
        <strong>${this.translateService.instant('COMMON.LABELS.ALTITUDE_CONFLICT_AT')} ${altitudeOrigin}${this.translateService.instant('COMMON.LABELS.M')}</strong><br>
      </div>
    `;
  }

  /** Draws coordination conflicts. */
  drawCoordinationConflicts(operationConflicts: any): void {
    operationConflicts?.conflictSegments?.forEach((segment: any) => {
      const conflictSegment = L.polyline(
        [
          [segment?.orig?.latitude, segment?.orig?.longitude],
          [segment?.dest?.latitude, segment?.dest?.longitude],
        ],
        { color: 'magenta', weight: 4 }
      );

      conflictSegment.on('mouseover', () => {
        conflictSegment.bindPopup(this.obtainConflictsPopUp(segment?.orig?.altitude, segment?.dest?.altitude), {
          className: 'customPopup',
        }).openPopup();
      });
      conflictSegment.on('mouseout', () => conflictSegment.closePopup());
      this.coordinationConflictLayer.addLayer(conflictSegment);
    });

    operationConflicts?.conflictPolygons?.forEach((polygon: any) => {
      const conflictPolygon = L.polyline(
        [
          [polygon?.orig?.latitude, polygon?.orig?.longitude],
          [polygon?.dest?.latitude, polygon?.dest?.longitude],
        ],
        { color: 'orangered', weight: 4 }
      );

      conflictPolygon.on('mouseover', () => {
        conflictPolygon.bindPopup(this.obtainConflictsPopUp(polygon?.orig?.altitude, polygon?.dest?.altitude), {
          className: 'customPopup',
        }).openPopup();
      });
      conflictPolygon.on('mouseout', () => conflictPolygon.closePopup());
      this.coordinationConflictLayer.addLayer(conflictPolygon);
    });

    this.coordinationConflictLayer.setZIndex(1000);
  }

  /** Draws one AP trajectory centroid. */
  drawAutopilotTrajectoryCentroid(apTrajectory: any): void {
    const centroid = L.marker([apTrajectory?.centroid?.latitude, apTrajectory?.centroid?.longitude], {
      icon: createDroneIcon('var(--color-ext-yelloworange)', 30, '#000000', 4),
    })
      .bindPopup(apTrajectory?.name)
      .on('click', () => this.trajectorySelected.emit(apTrajectory?.uniqueId));

    this.apTrajectoryLayerGroup.addLayer(centroid);
  }

  /** Draws one full AP trajectory. */
  drawAutopilotTrajectory(apTrajectory: any): void {
    let nextCommandIndex = 0;

    apTrajectory?.allCommandList?.forEach((command: any) => {
      if (nextCommandIndex === command.index) {
        let nextCommand = command?.nextCommandIdx
          ? apTrajectory.allCommandList.find((cmd: any) => cmd.index === command.nextCommandIdx)
          : null;

        if (nextCommand?.apCommand === 'SetSpeed') {
          nextCommand = nextCommand?.nextCommandIdx
            ? apTrajectory.allCommandList.find((cmd: any) => cmd.index === nextCommand.nextCommandIdx)
            : null;
        }

        if (!command?.label) {
          command.label = command?.apCommand;
        }

        if (command?.point) {
          switch (command.apCommand) {
            case 'Loiter':
            case 'SetWaypoint':
              this.drawWaypoint(command, command?.label);
              if (nextCommand) {
                this.drawSegment(command, nextCommand);
              }
              break;
            case 'MissionArea':
              this.drawWaypoint(command, command?.label);
              this.drawMissionArea(command);
              break;
            case 'SetSpeed':
              break;
            default:
              this.drawWaypoint(command);
              if (nextCommand && (nextCommand.apCommand === 'SetWaypoint' || nextCommand.apCommand === 'Loiter')) {
                this.drawSegment(command, nextCommand);
              }
              break;
          }
        }

        nextCommandIndex = command.nextCommandIdx;
      }
    });
  }

  /** Centers map using either regions or markers. */
  centerGroupOrRegion(regionMap: any, markers: L.LatLng[]): void {
    const bounds = L.latLngBounds([]);

    if (regionMap) {
      if (regionMap?.polygonLayer) {
        bounds.extend(regionMap.polygonLayer.getBounds());
      } else if (regionMap?.regions?.length > 0) {
        regionMap.regions.forEach((regionRef: any) => {
          const region = regionRef.region;
          region?.rset?.forEach((point: any) => bounds.extend(new L.LatLng(point?.latitude, point?.longitude)));
        });
      } else if (regionMap?.tracks?.length > 0) {
        regionMap.tracks.forEach((trackRef: any) => {
          const track = trackRef.trackSequence;
          track?.tsequence?.forEach((point: any) => bounds.extend(new L.LatLng(point?.latitude, point?.longitude)));
        });
      }
    }

    if (markers) {
      markers = markers.filter((value, index, arr) => {
        return index === arr.findIndex((obj) => obj?.lat === value?.lat && obj?.lng === value?.lng);
      });
      markers.forEach((marker) => bounds.extend(marker));
    }

    if (bounds.isValid()) {
      if (markers && markers.length === 1 && (!regionMap || (!regionMap.polygonLayer && (!regionMap.regions || regionMap.regions.length === 0)))) {
        this.centerByRadius(markers[0], 3000);
      } else {
        this.leafletmap.fitBounds(bounds);
      }
    }
  }

  /** Centers map using one point and radius. */
  centerByRadius(center: L.LatLng, radius: number): void {
    this.leafletmap.fitBounds(L.latLng(center.lat, center.lng).toBounds(radius));
  }

  /** Centers PSO selection with left padding. */
  centerByPSOBasicArea(markers?: L.LatLng[], center?: L.LatLng, radius?: number): void {
    const mapSize = this.leafletmap.getSize();
    const leftPadding = mapSize.x * 0.25;
    const animate = true;
    const duration = 0.8;
    const easeLinearity = 1;

    if (markers?.length > 0) {
      const uniqueMarkers = markers.filter((value, index, arr) => {
        return index === arr.findIndex((obj) => obj?.lat === value?.lat && obj?.lng === value?.lng);
      });

      if (uniqueMarkers.length > 1) {
        const bounds = L.latLngBounds([]);
        uniqueMarkers.forEach((marker) => bounds.extend(marker));

        if (bounds.isValid()) {
          const calculatedZoom = this.leafletmap.getBoundsZoom(bounds, false, L.point(leftPadding, 0));
          this.leafletmap.fitBounds(bounds, {
            paddingTopLeft: [leftPadding, 0],
            paddingBottomRight: [0, 0],
            maxZoom: calculatedZoom - 1,
            animate,
            duration,
            easeLinearity,
          });
        }
      } else if (uniqueMarkers.length === 1) {
        const bounds = L.latLng(uniqueMarkers[0].lat, uniqueMarkers[0].lng).toBounds(radius || 3000);
        const calculatedZoom = this.leafletmap.getBoundsZoom(bounds, false, L.point(leftPadding, 0));
        this.leafletmap.fitBounds(bounds, {
          paddingTopLeft: [leftPadding, 0],
          paddingBottomRight: [0, 0],
          maxZoom: calculatedZoom - 1,
          animate,
          duration,
          easeLinearity,
        });
      }
    } else if (center && radius) {
      const bounds = L.latLng(center.lat, center.lng).toBounds(radius);
      const calculatedZoom = this.leafletmap.getBoundsZoom(bounds, false, L.point(leftPadding, 0));
      this.leafletmap.fitBounds(bounds, {
        paddingTopLeft: [leftPadding, 0],
        paddingBottomRight: [0, 0],
        maxZoom: calculatedZoom - 1,
        animate,
        duration,
        easeLinearity,
      });
    }
  }

  /** Starts drawing a new region on the fly. */
  drawNewRegion(color: string): Promise<L.Polygon> {
    return new Promise((resolve, reject) => {
      const currentDrawRef = new (L as any).Draw.Polygon(this.leafletmap as any);

      const onCreated = (event: any) => {
        this.leafletmap.off('draw:created', onCreated);
        this.leafletmap.off('draw:cancel', onCancel);

        const layer: L.Polygon = event.layer;
        layer.setStyle({ color, fillColor: color, fillOpacity: 0.3 });
        this.polygonLayer.addLayer(layer);
        resolve(layer);
      };

      const onCancel = () => {
        this.leafletmap.off('draw:created', onCreated);
        this.leafletmap.off('draw:cancel', onCancel);
        currentDrawRef.disable();
        reject(new Error('Polygon drawing canceled'));
      };

      currentDrawRef.enable();
      this.leafletmap.on('draw:created', onCreated);
      this.leafletmap.on('draw:cancel', onCancel);
    });
  }

  /** Removes a temporary region from the map. */
  removeRegionArea(layer: L.Polygon): void {
    this.leafletmap.removeLayer(layer);
  }

  public getOverlayLayerMap(): Record<string, L.LayerGroup> {
    return {
      structures: this.structuresLayerGroup,
      protectionLow: this.protectionLowLayerGroup,
      protectionHigh: this.protectionHighLayerGroup,
      ctrAtz: this.ctrAtzLayerGroup,
      naturalParks: this.naturalParksLayerGroup,
      harbours: this.harboursLayerGroup,
      innerZones: this.innerZonesLayerGroup,
      runways: this.runwayLayerGroup,
      others: this.otherLayerGroup,
      markers: this.markersLayerGroup,
      operationMarkers: this.apOperationLayerGroup,
      trajectories: this.apTrajectoryLayerGroup,
      conflicts: this.coordinationConflictLayer,
      polygons: this.polygonLayer,
      basicAreaLevel0: this.basicAreaLevel0LayerGroup,
      basicAreaLevel1: this.basicAreaLevel1LayerGroup,
      selectedBasicAreaLevel0: this.selectedBasicAreaLevel0Layer,
      selectedBasicAreaLevel1: this.selectedBasicAreaLevel1Layer,
      communications: this.communicationsLayerGroup,
      externalRadius: this.externalRadiusLayer,
      externalRectangle: this.externalRectangleLayer,
      externalConvexPolygon: this.externalConvexPolygonLayer,
      reducedRegionApproximation: this.reducedRegionApproximationLayer,
      regionMap: this.regionMapLayer,
    };
  }

  /** Exposes the live Leaflet map instance for child components using @ViewChild. */
  get map(): L.Map {
    return this.leafletmap;
  }

  /** Exposes all shared layer groups so composing components can draw into them. */
  get layerRefs(): MapLayerRefs {
    return {
      structuresLayerGroup: this.structuresLayerGroup,
      innerZonesLayerGroup: this.innerZonesLayerGroup,
      runwayLayerGroup: this.runwayLayerGroup,
      protectionLowLayerGroup: this.protectionLowLayerGroup,
      protectionHighLayerGroup: this.protectionHighLayerGroup,
      ctrAtzLayerGroup: this.ctrAtzLayerGroup,
      naturalParksLayerGroup: this.naturalParksLayerGroup,
      harboursLayerGroup: this.harboursLayerGroup,
      trackNetworkLayerGroup: this.trackNetworkLayerGroup,
      urbanAreasLayerGroup: this.urbanAreasLayerGroup,
      urbanAreasRegistryLayerGroup: this.urbanAreasRegistryLayerGroup,
      otherLayerGroup: this.otherLayerGroup,
      markersLayerGroup: this.markersLayerGroup,
    };
  }

  /** Registers an additional layer into the panel registry (used by embedding parent components). */
  registerLayer(def: MapLayerDefinition): void {
    this.layerRegistry.registerLayer(def);
  }

  /** Removes a layer from the panel registry (used by embedding parent components). */
  unregisterLayer(id: string): void {
    this.layerRegistry.unregisterLayer(id);
  }

  public getLayerBounds(layer: L.Layer): L.LatLngBounds | null {
    const bounds = new L.LatLngBounds([]);
    const anyLayer = layer as any;

    if (typeof anyLayer.getBounds === 'function') {
      const layerBounds = anyLayer.getBounds();
      return layerBounds?.isValid?.() ? layerBounds : null;
    }

    if (typeof anyLayer.getLatLng === 'function') {
      bounds.extend(anyLayer.getLatLng());
      return bounds.isValid() ? bounds : null;
    }

    if (typeof anyLayer.eachLayer === 'function') {
      anyLayer.eachLayer((child: any) => {
        if (typeof child.getBounds === 'function') {
          const childBounds = child.getBounds();
          if (childBounds?.isValid?.()) {
            bounds.extend(childBounds);
          }
        } else if (typeof child.getLatLng === 'function') {
          bounds.extend(child.getLatLng());
        }
      });
      return bounds.isValid() ? bounds : null;
    }

    return null;
  }

  /**
   * Draws an operation on the map (route + start/end markers).
   * Adapter method for pages that use the old API.
   */
  drawOperation(route: any, startMarker: L.CircleMarker, endMarker: L.CircleMarker): void {
    if (route) {
      this.apOperationLayerGroup.addLayer(route);
    }
    if (startMarker) {
      this.apOperationLayerGroup.addLayer(startMarker);
    }
    if (endMarker) {
      this.apOperationLayerGroup.addLayer(endMarker);
    }
  }

  /**
   * Clears operation layer group (typo-compatible alias).
   * Adapter method for pages using the old API name.
   */
  clearOPerationLayerGroup(): void {
    this.clearOperationLayers();
  }

  /**
   * Centers map by fitting to bounds.
   * Adapter method for pages that use the old API.
   */
  centerByBounds(bounds: L.LatLngBounds): void {
    if (bounds && bounds.isValid()) {
      this.leafletmap.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
    }
  }

  /**
   * Highlights a position on the map (used for chart point hover).
   * Shows a temporary marker at the given coordinates.
   */
  highlightHoverPosition(lat: number, lon: number): void {
    // Clear previous hover marker if it exists
    this.clearHoverMarker();

    // Create a new hover marker
    this.hoverMarker = new L.CircleMarker([lat, lon], {
      radius: 8,
      fillColor: '#3388ff',
      color: '#0066cc',
      weight: 2,
      opacity: 0.8,
      fillOpacity: 0.6,
      pane: 'markerPane',
    });

    // Add to map
    this.hoverMarker.addTo(this.leafletmap);
  }

  /**
   * Clears the hover marker from the map.
   */
  clearHoverMarker(): void {
    if (this.hoverMarker) {
      this.leafletmap.removeLayer(this.hoverMarker);
      this.hoverMarker = null;
    }
  }




}